import React from 'react';
import { Sliders, Clock } from 'lucide-react';

export const EffectsPanel = ({ track, projectTempo, onUpdateEffect, onUpdateSampleBpm, onToggleSampleSync }) => {
  return (
    <div className="p-4 h-full overflow-y-auto">
      <h3 className="text-sm font-bold mb-4 flex items-center gap-2">
        <Sliders className="w-4 h-4" /> FX: {track.name}
      </h3>
      
      {/* BPM Sync Section - Only for user samples */}
      {track.isUserSample && (
        <div className="mb-6 p-3 bg-[#0a0a0a] rounded-lg border border-orange-500/30">
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs text-orange-400 font-medium flex items-center gap-1">
              <Clock className="w-3 h-3" /> Sample BPM Sync
            </label>
            <button
              onClick={onToggleSampleSync}
              className={`px-2 py-0.5 rounded text-[10px] font-bold transition-colors ${
                track.syncToProjectTempo 
                  ? 'bg-green-500 text-white' 
                  : 'bg-[#333] text-gray-400'
              }`}
              data-testid="sync-toggle"
            >
              {track.syncToProjectTempo ? 'SYNC ON' : 'SYNC OFF'}
            </button>
          </div>
          
          <div className="space-y-2">
            <div>
              <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                <span>Sample Original BPM</span>
                <span className="text-orange-400 font-mono">{track.sampleBpm || 120}</span>
              </div>
              <input
                type="number"
                min="40"
                max="240"
                value={track.sampleBpm || 120}
                onChange={(e) => onUpdateSampleBpm(parseInt(e.target.value) || 120)}
                className="w-full bg-[#1a1a1a] text-white text-center rounded px-2 py-1.5 text-sm font-mono border border-[#333] focus:border-orange-500 focus:outline-none"
                data-testid="sample-bpm-input"
              />
            </div>
            
            <div className="flex justify-between text-[10px] text-gray-500">
              <span>Project Tempo</span>
              <span className="text-blue-400 font-mono">{projectTempo} BPM</span>
            </div>
            
            {track.syncToProjectTempo && track.sampleBpm > 0 && (
              <div className="text-[10px] text-center pt-2 border-t border-[#333]">
                <span className="text-gray-500">Playback Rate: </span>
                <span className="text-green-400 font-mono font-bold">
                  {(projectTempo / track.sampleBpm).toFixed(2)}x
                </span>
                {projectTempo > track.sampleBpm ? (
                  <span className="text-yellow-400 ml-1">(faster)</span>
                ) : projectTempo < track.sampleBpm ? (
                  <span className="text-blue-400 ml-1">(slower)</span>
                ) : (
                  <span className="text-green-400 ml-1">(matched)</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Reverb */}
      <div className="mb-6">
        <label className="text-xs text-gray-400 block mb-2">Reverb</label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={track.reverbWet || 0}
          onChange={(e) => onUpdateEffect('reverbWet', parseFloat(e.target.value))}
          className="w-full accent-purple-500"
        />
        <div className="text-xs text-gray-500 mt-1">{Math.round((track.reverbWet || 0) * 100)}%</div>
      </div>
      
      {/* Delay */}
      <div className="mb-6">
        <label className="text-xs text-gray-400 block mb-2">Delay</label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={track.delayWet || 0}
          onChange={(e) => onUpdateEffect('delayWet', parseFloat(e.target.value))}
          className="w-full accent-blue-500"
        />
        <div className="text-xs text-gray-500 mt-1">{Math.round((track.delayWet || 0) * 100)}%</div>
      </div>
      
      {/* EQ */}
      <div className="mb-4">
        <label className="text-xs text-gray-400 block mb-2">EQ</label>
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-[10px] text-gray-500 mb-1">
              <span>Low</span>
              <span>{track.eqLow || 0} dB</span>
            </div>
            <input type="range" min="-12" max="12" value={track.eqLow || 0} onChange={(e) => onUpdateEffect('eqLow', parseInt(e.target.value))} className="w-full accent-red-500" />
          </div>
          <div>
            <div className="flex justify-between text-[10px] text-gray-500 mb-1">
              <span>Mid</span>
              <span>{track.eqMid || 0} dB</span>
            </div>
            <input type="range" min="-12" max="12" value={track.eqMid || 0} onChange={(e) => onUpdateEffect('eqMid', parseInt(e.target.value))} className="w-full accent-yellow-500" />
          </div>
          <div>
            <div className="flex justify-between text-[10px] text-gray-500 mb-1">
              <span>High</span>
              <span>{track.eqHigh || 0} dB</span>
            </div>
            <input type="range" min="-12" max="12" value={track.eqHigh || 0} onChange={(e) => onUpdateEffect('eqHigh', parseInt(e.target.value))} className="w-full accent-green-500" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EffectsPanel;
